export type VialType = "wholeBlood" | "plasma";

export const vialTypeLabels: Record<VialType, string> = {
  wholeBlood: "Whole Blood",
  plasma: "Plasma",
};

export const vialBarcodePrefixes: Record<VialType, string> = {
  wholeBlood: "WB",
  plasma: "PL",
};

export function getVialTypeFromBarcode(
  barcodeId: string
): VialType | undefined {
  if (barcodeId.startsWith(vialBarcodePrefixes.wholeBlood)) {
    return "wholeBlood";
  }
  if (barcodeId.startsWith(vialBarcodePrefixes.plasma)) {
    return "plasma";
  }
  return undefined;
}

export function assignedVialTypes(
  samples: readonly { barcodeId?: string | null }[] | undefined
): Partial<Record<VialType, string>> {
  const map: Partial<Record<VialType, string>> = {};
  for (const sample of samples ?? []) {
    if (!sample.barcodeId) {
      continue;
    }
    const type = getVialTypeFromBarcode(sample.barcodeId);
    if (type) {
      map[type] = sample.barcodeId;
    }
  }
  return map;
}

export function isBloodWorkComplete(
  samples: readonly { barcodeId?: string | null }[] | undefined
): boolean {
  const assigned = assignedVialTypes(samples);
  return assigned.wholeBlood != null && assigned.plasma != null;
}

export type BloodWorkStatus = "complete" | "partial" | "missing";

export function bloodWorkStatus(
  samples: readonly { barcodeId?: string | null }[] | undefined
): BloodWorkStatus {
  const assigned = assignedVialTypes(samples);
  const count =
    (assigned.wholeBlood != null ? 1 : 0) + (assigned.plasma != null ? 1 : 0);
  if (count === 2) {
    return "complete";
  }
  if (count === 1) {
    return "partial";
  }
  return "missing";
}

export type ScanAction =
  | { kind: "invalidPrefix"; prefix: string }
  | { kind: "assign"; vialType: VialType; barcodeId: string }
  | { kind: "alreadyAssigned"; vialType: VialType; barcodeId: string }
  | { kind: "needsReassignMode"; vialType: VialType }
  | { kind: "wrongVialForReassign"; reassigning: VialType; scanned: VialType }
  | { kind: "reassign"; vialType: VialType; newBarcodeId: string };

export function decideScanAction({
  barcodeId,
  assignedByType,
  reassigningType,
}: {
  barcodeId: string;
  assignedByType: Partial<Record<VialType, string>>;
  reassigningType: VialType | undefined;
}): ScanAction {
  const vialType = getVialTypeFromBarcode(barcodeId);
  if (!vialType) {
    return { kind: "invalidPrefix", prefix: barcodeId.slice(0, 2) };
  }

  const existing = assignedByType[vialType];
  if (existing == null) {
    return { kind: "assign", vialType, barcodeId };
  }
  if (existing === barcodeId) {
    return { kind: "alreadyAssigned", vialType, barcodeId };
  }
  if (reassigningType === undefined) {
    return { kind: "needsReassignMode", vialType };
  }
  if (reassigningType !== vialType) {
    return {
      kind: "wrongVialForReassign",
      reassigning: reassigningType,
      scanned: vialType,
    };
  }
  return { kind: "reassign", vialType, newBarcodeId: barcodeId };
}
